import * as THREE from 'three';

const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _q3 = new THREE.Quaternion();
const _v3 = new THREE.Vector3();

interface BoneRest {
  bone: THREE.Bone;
  localQ: THREE.Quaternion;
  localP: THREE.Vector3;
  /** Rotation de repos dans le repère personnage (Human.root). */
  charQ: THREE.Quaternion;
  /** Position de repos (tête de l'os) dans le repère personnage. */
  charP: THREE.Vector3;
  /** Direction de l'os (axe Y local, tête→queue) au repos, repère personnage. */
  dir: THREE.Vector3;
  /** Rotation additive de la frame, exprimée dans le repère personnage. */
  delta: THREE.Quaternion;
  /** Rotation additive locale (après conversion), ex. flexion des doigts autour de X. */
  localDelta: THREE.Quaternion;
  touched: boolean;
}

/**
 * Pilote de squelette par couches additives.
 * Chaque frame : begin() → les couches empilent des rotations (repère personnage ou local) → apply().
 * Les rotations "repère personnage" sont conjuguées par la rotation de repos de l'os :
 *   local = repos_local · (R⁻¹ · D · R)  ⇒  monde = D · R  (D appliqué autour de la tête de l'os).
 */
export class Rig {
  readonly rest = new Map<string, BoneRest>();
  readonly root: THREE.Object3D;
  rootOffset = new THREE.Vector3();
  private rootBone?: BoneRest;

  constructor(root: THREE.Object3D, bones: ReadonlyMap<string, THREE.Bone>) {
    this.root = root;
    for (const [name, bone] of bones) {
      this.rest.set(name, {
        bone, localQ: new THREE.Quaternion(), localP: new THREE.Vector3(), charQ: new THREE.Quaternion(), charP: new THREE.Vector3(),
        dir: new THREE.Vector3(0, 1, 0), delta: new THREE.Quaternion(), localDelta: new THREE.Quaternion(), touched: false,
      });
    }
    this.captureRest();
  }

  /** À appeler quand les os sont dans leur pose de repos (création, après setParams). */
  captureRest() {
    for (const r of this.rest.values()) {
      // Le module human expose le repos dans userData (setParams conserve la pose courante).
      const ud = r.bone.userData as { restQuaternion?: THREE.Quaternion; restPosition?: THREE.Vector3 };
      r.localQ.copy(ud.restQuaternion ?? r.bone.quaternion);
      r.localP.copy(ud.restPosition ?? r.bone.position);
    }
    // Rotations/positions de repos dans le repère personnage, en descendant la hiérarchie.
    const visit = (r: BoneRest, parent?: BoneRest) => {
      if (parent) {
        r.charQ.copy(parent.charQ).multiply(r.localQ);
        r.charP.copy(r.localP).applyQuaternion(parent.charQ).add(parent.charP);
      } else {
        // Os racine : son parent est (ou est sous) Human.root.
        this.root.updateWorldMatrix(true, false);
        r.bone.parent!.updateWorldMatrix(true, false);
        _m.copy(this.root.matrixWorld).invert().multiply(r.bone.parent!.matrixWorld);
        _m.decompose(_v, _q, _v2);
        r.charQ.copy(_q).multiply(r.localQ);
        r.charP.copy(r.localP).applyMatrix4(_m);
        this.rootBone = r;
      }
      r.dir.set(0, 1, 0).applyQuaternion(r.charQ);
      for (const c of r.bone.children) {
        const cr = (c as THREE.Bone).isBone ? this.byBone(c as THREE.Bone) : undefined;
        if (cr) visit(cr, r);
      }
    };
    for (const r of this.rest.values()) {
      if (!(r.bone.parent as THREE.Bone)?.isBone) visit(r);
    }
  }

  private byBoneCache = new Map<THREE.Bone, BoneRest>();
  private byBone(b: THREE.Bone) {
    if (!this.byBoneCache.size) for (const r of this.rest.values()) this.byBoneCache.set(r.bone, r);
    return this.byBoneCache.get(b);
  }

  get(name: string) { return this.rest.get(name); }

  begin() {
    for (const r of this.rest.values()) {
      r.delta.identity();
      r.localDelta.identity();
      r.touched = false;
    }
    this.rootOffset.set(0, 0, 0);
  }

  /** Ajoute une rotation (repère personnage) autour d'un axe. */
  rotate(name: string, axis: THREE.Vector3, angle: number) {
    const r = this.rest.get(name);
    if (!r || angle === 0) return;
    _q.setFromAxisAngle(axis, angle);
    r.delta.premultiply(_q);
    r.touched = true;
  }

  /** Ajoute une rotation quaternion (repère personnage). */
  rotateQ(name: string, q: THREE.Quaternion) {
    const r = this.rest.get(name);
    if (!r) return;
    r.delta.premultiply(q);
    r.touched = true;
  }

  /** Fait pivoter l'os de `angle` rad de sa direction de repos vers `toward` (repère personnage). */
  bendToward(name: string, toward: THREE.Vector3, angle: number) {
    const r = this.rest.get(name);
    if (!r || angle === 0) return;
    _v.crossVectors(r.dir, toward);
    if (_v.lengthSq() < 1e-8) return;
    _v.normalize();
    _q.setFromAxisAngle(_v, angle);
    r.delta.premultiply(_q);
    r.touched = true;
  }

  /** Rotation dans le repère local de l'os (ex. flexion des doigts autour de X local). */
  rotateLocal(name: string, axis: THREE.Vector3, angle: number) {
    const r = this.rest.get(name);
    if (!r || angle === 0) return;
    _q.setFromAxisAngle(axis, angle);
    r.localDelta.multiply(_q);
    r.touched = true;
  }

  /**
   * Pose de base : rotation LOCALE relative au repos (convention module human : bone.quaternion = repos · poseLocal).
   * À appeler juste après begin(), avant les couches additives. `weight` < 1 mélange avec le repos.
   */
  setPoseLocal(name: string, q: THREE.Quaternion, weight = 1) {
    const r = this.rest.get(name);
    if (!r) return;
    if (weight >= 1) r.localDelta.copy(q);
    else r.localDelta.identity().slerp(q, Math.max(0, weight));
    r.touched = true;
  }

  /** Multiplie une rotation locale additive (unités faciales osseuses…). */
  multiplyLocal(name: string, q: THREE.Quaternion) {
    const r = this.rest.get(name);
    if (!r) return;
    r.localDelta.multiply(q);
    r.touched = true;
  }

    /** Décalage de la racine (repère personnage, mètres) pour une pose (assise, accroupie…). */
  setRootOffset(v: THREE.Vector3) {
    this.rootOffset.copy(v);
  }

  /** Écrit les quaternions locaux à partir des couches. */
  apply() {
    for (const r of this.rest.values()) {
      if (!r.touched) {
        r.bone.quaternion.copy(r.localQ);
        continue;
      }
      // local = repos · (R⁻¹ D R) · deltaLocal
      _q2.copy(r.charQ).invert().multiply(r.delta).multiply(r.charQ);
      r.bone.quaternion.copy(r.localQ).multiply(_q2).multiply(r.localDelta);
    }
    if (this.rootBone) {
      // Décalage de la racine (repère personnage → repère parent de l'os racine).
      this.rootBone.bone.position.copy(this.rootBone.localP).add(this.rootOffset);
    }
  }

  /** Remet le squelette exactement au repos (avant un setParams par exemple). */
  resetToRest() {
    for (const r of this.rest.values()) {
      r.bone.quaternion.copy(r.localQ);
      r.bone.position.copy(r.localP);
    }
  }

  /**
   * Oriente un os (ex. œil) pour que son axe Y local vise un point monde, en partant de son
   * orientation de repos sous le parent ANIMÉ. Limité à `maxAngle`. Écrit directement le quaternion.
   */
  aim(name: string, targetWorld: THREE.Vector3, maxAngle: number, out?: { yaw: number; pitch: number }) {
    const r = this.rest.get(name);
    if (!r) return;
    const parent = r.bone.parent!;
    parent.updateWorldMatrix(true, false);
    parent.getWorldQuaternion(_q);                 // rotation monde du parent animé
    const restWorld = _q2.copy(_q).multiply(r.localQ);
    const fwd = _v.set(0, 1, 0).applyQuaternion(restWorld);
    _m.copy(parent.matrixWorld);
    const head = _v2.copy(r.localP).applyMatrix4(_m);
    const dir = head.subVectors(targetWorld, head).normalize();
    let angle = fwd.angleTo(dir);
    if (angle > maxAngle) {
      // Limite l'angle en restant dans le plan fwd/dir.
      const axis = _v3.crossVectors(fwd, dir).normalize();
      dir.copy(fwd).applyAxisAngle(axis, maxAngle);
      angle = maxAngle;
    }
    if (out) {
      // Composantes approximatives (repère monde) pour piloter les paupières.
      out.pitch = Math.asin(THREE.MathUtils.clamp(dir.y, -1, 1)) - Math.asin(THREE.MathUtils.clamp(fwd.y, -1, 1));
      out.yaw = Math.atan2(dir.x, dir.z) - Math.atan2(fwd.x, fwd.z);
    }
    const rot = _q3.setFromUnitVectors(fwd, dir);
    // monde voulu = rot · restWorld  ⇒  local = parent⁻¹ · monde
    const world = rot.multiply(restWorld);
    r.bone.quaternion.copy(_q.invert().multiply(world));
  }
}
